import { Comment } from "@/components/comments/Comment.tsx";
import { FormResultForEmployeeProps } from "@/components/form-result/result-modal/components/types.ts";
import { CommentElement } from "@/components/questionnaire/components/CommentElement.tsx";
import { ScaleOptionButtons } from "@/components/questionnaire/components/ScaleOptionButtons.tsx";
import { useScaleDirection } from "@/components/questionnaire/components/use-scale-direction.hook.ts";
import { getFormKey } from "@/components/questionnaire/questionnaire.types.ts";
import { GoalCard } from "@/pages/talent/goals/components/GoalCard.tsx";
import { ApiFormViewElement } from "@/types/forms/form-view.types.ts";
import { Stack, Text, Title, HtmlViewer } from "@skailer-com/uikit";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface GoalsElementProps {
  element: ApiFormViewElement;
}

export const GoalsElement = ({ element }: GoalsElementProps) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const firstDirection = useScaleDirection(element.goals_settings?.form_scale?.scale);
  const secondDirection = useScaleDirection(element.goals_settings?.form_scale.scale2);

  const goalsSettings = element.goals_settings;
  if (!goalsSettings) return null;

  const scaleFirst = goalsSettings.form_scale.scale.sort((a, b) => a.sort - b.sort);
  const scaleSecond = goalsSettings.form_scale.scale2?.sort((a, b) => a.sort - b.sort);

  const formElementName = getFormKey(element);
  const hasError = !!errors[formElementName];
  const isEmptyGoals =
    !!element.empty_goals && (element.goals ?? []).length == 0 && !goalsSettings.show_when_no_goals;

  return (
    <Stack gap={"2xl"} data-error={hasError || undefined}>
      {(goalsSettings.title || goalsSettings.description) && (
        <Stack gap="md">
          <Title
            header={goalsSettings.title || undefined}
            size={"md"}
            required={goalsSettings.is_required}
          />
          {goalsSettings.description && <HtmlViewer content={goalsSettings.description} />}
        </Stack>
      )}
      <Stack gap={"md"}>
        {element.goals?.map((goal) => (
          <GoalCard
            key={goal.id}
            goal={goal}
            disableDecoration={true}
            target={"_blank"}
            to={`/goals/${goal.id}`}
          />
        ))}

        {isEmptyGoals ? (
          <Text className="text-secondary">{t("performance_review:no_goals_to_evaluate")}</Text>
        ) : (
          <Controller
            name={formElementName ?? ""}
            control={control}
            render={({ field: { value, onChange } }) => {
              const values = Array.isArray(value) ? (value as number[]) : [];
              const secondIds = scaleSecond?.map((s) => s.id) || [];
              const handleFirst = (id: number) =>
                onChange([...values.filter((v) => secondIds.includes(v)), id]);
              return (
                <ScaleOptionButtons
                  scale={scaleFirst}
                  selectedIds={values}
                  direction={firstDirection}
                  onSelect={handleFirst}
                  error={String(errors[formElementName ?? ""]?.message || "")}
                />
              );
            }}
          />
        )}
        {!isEmptyGoals && scaleSecond && (
          <Controller
            name={formElementName ?? ""}
            control={control}
            render={({ field: { value, onChange } }) => {
              const values = Array.isArray(value) ? (value as number[]) : [];
              const firstIds = scaleFirst.map((s) => s.id);
              const handleSecond = (id: number) =>
                onChange([...values.filter((v) => firstIds.includes(v)), id]);
              return (
                <ScaleOptionButtons
                  scale={scaleSecond}
                  selectedIds={values}
                  direction={secondDirection}
                  onSelect={handleSecond}
                  error={String(errors[formElementName ?? ""]?.message || "")}
                />
              );
            }}
          />
        )}
      </Stack>
      {goalsSettings.is_comment_enabled && <CommentElement element={element} />}
    </Stack>
  );
};

export const GoalsElementResult = ({ element, answer }: FormResultForEmployeeProps) => {
  const { t } = useTranslation();

  const firstDirection = useScaleDirection(element.goals_settings?.form_scale?.scale);
  const secondDirection = useScaleDirection(element.goals_settings?.form_scale.scale2);

  const goalsSettings = element.goals_settings;
  if (!goalsSettings) return null;

  const selectedIds = answer?.answer_scale_option_ids || [];
  const scaleFirst = goalsSettings.form_scale.scale.sort((a, b) => a.sort - b.sort);
  const scaleSecond = goalsSettings.form_scale.scale2?.sort((a, b) => a.sort - b.sort);

  return (
    <>
      <Title
        header={goalsSettings.title || t("performance_review:question_type_goal")}
        size={"sm"}
        paddingTop={true}
        className={!goalsSettings.title ? "text-secondary" : ""}
      />
      <Stack gap={"md"} className={"mt-3"}>
        {element.goals?.map((goal) => (
          <GoalCard
            key={goal.id}
            goal={goal}
            disableDecoration={true}
            target={"_blank"}
            to={`/goals/${goal.id}`}
          />
        ))}

        {selectedIds.length === 0 && (element.goals ?? []).length === 0 ? (
          <Text className={"text-secondary text-sm"}>
            {t("performance_review:no_goals_to_evaluate")}
          </Text>
        ) : (
          <>
            <ScaleOptionButtons
              scale={scaleFirst}
              selectedIds={selectedIds}
              direction={firstDirection}
            />

            {scaleSecond && (
              <ScaleOptionButtons
                scale={scaleSecond}
                selectedIds={selectedIds}
                direction={secondDirection}
              />
            )}
          </>
        )}
        {answer && answer.comment && (
          <Comment text={answer.comment} employee={null} isShort={false} />
        )}
      </Stack>
    </>
  );
};
